/**
 * How far along tonight is, drawn as a horizontal bar with two segments (GH-781).
 *
 * ## Why this is not the display's thermometer
 *
 * `live-display` draws a vertical SVG tube sized for a four-metre projection, with tick labels a
 * hall can read from the back row. It cannot be imported here — the two bundles share only
 * `@givehere/contracts` and `@givehere/ui` — and it should not be: a phone in somebody's hand is a
 * different instrument, and it needs one thing the wall's does not, which is a way to show that
 * part of the total is a **promise** rather than money that arrived.
 *
 * ## The hatch is a texture, and that is the point
 *
 * The pledged segment is drawn in `--warning` as diagonal hatching, which is the one pattern the
 * design language admits (`docs/design/lantern/ADDENDUM.md` §14) and it admits it in warning and
 * never in brass — *"brass on a warning would put the hall's dignity on a hazard."* Warning is
 * also the right tone on its own terms: a pledge is not an error and it is not a payment, and red
 * or green would each say one of those two wrong things.
 *
 * Because the difference between the segments is texture and not only colour, the bar survives
 * greyscale, `forced-colors`, and every kind of colour blindness (SPEC.md §17.2) — and the line
 * beneath it names both amounts in words regardless, so nothing here is carried by the drawing
 * alone.
 *
 * ## What it refuses to draw
 *
 *  - **No bar at all when no goal is set.** A track against a target of zero looks either empty
 *    or full, and both are a claim about a number nobody entered.
 *  - **No pledged segment unless the operator switched pledges in *and* somebody has pledged.**
 *    An event that does not count promises must show none; an event that counts them and has
 *    none must not show an empty stripe that reads as a debt.
 *
 * The figure and the split line above and below it are the accessible statement of progress, so
 * the bar itself is `aria-hidden` and the one `role="img"` sentence beside it names both parts in
 * currency. That mirrors iOS `GoalHero`, whose thermometer is `accessibilityHidden` for the same
 * reason: two announcements of one fact is worse than one.
 */
import { formatMoney, type GoalResponse } from "@givehere/contracts";
import { countsPledges, goalPercents } from "./goal-math.js";

export function GoalBar({ goal }: { goal: GoalResponse }) {
  // The percentages are computed as a clamped pair — see `goalPercents`. Reading them apart and
  // clamping each to 100 here would paint the pledged block past the end of the track on a night
  // that is 90% received and 30% pledged.
  const { received, pledged } = goalPercents(goal);
  const showsPledge = countsPledges(goal);

  if (BigInt(goal.goal.amountMinor) <= 0n) return null;

  return (
    <div
      className="goalbar"
      role="img"
      aria-label={
        showsPledge
          ? `${formatMoney(goal.received)} received and ${formatMoney(goal.pledged)} pledged towards ${formatMoney(goal.goal)}.`
          : `${formatMoney(goal.received)} received towards ${formatMoney(goal.goal)}.`
      }
    >
      {/*
        Inline widths because they are data, not design: the two numbers change every few seconds
        and no stylesheet can hold them. Everything else about this bar — colour, height, radius,
        the hatch, the transition — is in `styles.css` where a design token can reach it.

        `inline-size` rather than `width`: `apps/surface-structure.test.ts` fails on a new physical
        edge in these sheets, and a bar that fills from the start of the line is the same bar in a
        right-to-left layout without a second rule.
      */}
      <span
        className="goalbar__received"
        style={{ inlineSize: `${received}%` }}
        aria-hidden="true"
      />
      {showsPledge && (
        <span
          className="goalbar__pledged"
          style={{ inlineSize: `${pledged}%` }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}
